import {
  Children,
  createContext,
  FC,
  ReactChild,
  ReactFragment,
  ReactNode,
  ReactPortal,
  useContext,
  useEffect,
  useRef,
  ReactElement,
} from 'react';
import { useImmer } from 'use-immer';
import { valueof } from 'react-joyride';
import { StepItemType } from 'new/elements/Form/components';

export type Action = {
  label: string;
  onClick?: () => void;
};

type LoaderMessages =
  | { [requestKey: string]: string }
  | string
  | { message: string; timeout: number }[];

export type FormContextType = {
  steps: StepItemType[];
  pages: (ReactChild | ReactFragment | ReactPortal)[];
  activePage?: number;
  showCancel?: boolean;
  finished?: boolean;
  awaitingValidation?: boolean;
  /**
   * When true, Continue stays disabled (e.g. credential probe in flight on Authenticate).
   * Cleared on active page change and when leaving that step (caller may reset on unmount).
   */
  continueBlockedExternally?: boolean;
  setContinueBlocked?: (blocked: boolean) => void;
  loaderMessages?: LoaderMessages;
  showLeaveConfirmation: boolean;
  confirmButtonTitle?: string;
  cancelButtonTitle?: string;
  setActivePage(page: number): void;
  /**
   * You can select whether the Cancel Button must be visible, or not.
   * @param visible
   * @returns
   */
  setCancelButtonVisible(visible: boolean): void;
  /**
   * It is used to track when some event happens around the FormProvider
   * @param event
   * @param data
   * @returns
   */
  sendEvent(event: EventTypesType, data?: any): void;
  /**
   * You can add an event to be listened to.
   * @param event
   * @param listener
   * @returns
   */
  addFormEventListener(event: string, listener: (data: any) => void): void;
  /**
   * You can remove an event that has been listened to.
   * @param event
   * @param listener
   * @returns
   */
  removeFormEventListener(event: string, listener: (data: any) => void): void;
  /**
   * It will move to the next step if the current step is valid.
   * @param step page number
   * @param dismissValidation true if you want skip the validation
   * @returns
   */
  nextPage(step?: number, dismissValidation?: boolean): Promise<void>;
  /**
   * It will validate the current step.
   * We expose it to make it possible to be manually triggered.
   * @returns
   */
  validate(): Promise<boolean>;
  /**
   * Use this to move the form out of the awaiting validation state.
   */
  cancelValidation(): void;
  goBack(): Promise<void>;
  /**
   * You can set specific message based on requestKey, or a generic key.
   *
   * @HowToUse
   * setLoaderMessages({ [GET_CREDENTIAL_REQUEST]: 'Verifying credential' });
   * @param loaderMessages
   * @returns
   */
  setLoaderMessages?: (loaderMessages: LoaderMessages) => void;
  /**
   * You can set a validation function that receives a function or a promise,
   * which must return a boolean: true for valid or false for invalid.
   * Accepts either a single validator or an array of validators.
   * @param validator
   * @returns
   */
  setValidation: (validator: ValidatorType) => void;
  /**
   * You can add validation function that receive a function or a promise,
   * which must return a boolean: true for valid or false for invalid.
   * This is usually used when you need to add specific validations without overwriting the existing validations.
   * @param validator
   * @param key key to identify the validator (prevents duplicates)
   * @returns
   */
  addValidation: (validator: ValidatorType, key: string) => void;
  /**
   * You can override the default next function by setting a function that receives a function or a promise,
   * which must return a boolean: true for valid or false for invalid.
   * @param onNext
   * @returns
   */
  overrideNextFunction?: (onNext: SuccessFuncType) => void;
  /**
   * You can override the default back function by setting a function that receives a function or a promise,
   * which must return a boolean.
   * @param onNext
   * @returns
   */
  overrideBackFunction?: (onNext: BackFuncType) => void;
  /**
   * You can set whether to show a leave confirmation modal before leaving the form.
   * @param show : boolean
   * @returns
   */
  setShowLeaveConfirmation: (show: boolean) => void;
  /**
   * You can set a new confirmation button title.
   * @param title
   * @returns
   */
  setConfirmButtonTitle?: (title: string) => void;
  /**
   * You can set a new cancel button title.
   * @param title
   * @returns
   */
  setCancelButtonTitle?: (title: string) => void;
};

type TypeForm = {
  type: string;
};

type PreStepsForm = {
  preSteps: boolean;
};

type TitleForm = {
  title: string;
};

type FormConfig = {
  formConfig: PreStepsForm | TitleForm;
};

export type FCFormPageType<T> = FC<T> & (TypeForm | FormConfig);
export type FormPageType = {
  type: {
    formConfig?: {
      title?: string;
    };
  };
} & ReactElement;

export interface FormProps {
  isModal?: boolean;
  isEditMode?: boolean;
  HeaderComponent?: ReactNode;
  StepsComponent?: ReactNode;
  containerClassName?: string;
  startPage?: number;
  children: FormPageType[];
  onDismiss?: () => boolean | void;
  title?: string;
  description?: string;
  formType?: string;
}

export const FormContext = createContext<FormContextType>({
  pages: [],
  steps: [],
  sendEvent: (event) => {},
  addFormEventListener: (event, listener) => {},
  removeFormEventListener: (event, listener) => {},
  setActivePage: (page) => {},
  setCancelButtonVisible: (visible) => {},
  setValidation: (validator) => {},
  addValidation: (validator, key) => {},
  nextPage: async () => {},
  goBack: async () => {},
  validate: async () => true,
  showLeaveConfirmation: false,
  setShowLeaveConfirmation: () => {},
  confirmButtonTitle: 'Continue',
  cancelButtonTitle: 'Discard Changes',
  cancelValidation: () => {},
  continueBlockedExternally: false,
  setContinueBlocked: () => {},
});

export function useForm() {
  return useContext(FormContext);
}

export const eventTypes = {
  DISMISS: 'dismiss',
  CANCEL: 'cancel',
  BACK: 'back',
  NEXT_CLICKED: 'next_clicked',
  NEXT_PAGE: 'next_page',
} as const;

type EventTypesType = valueof<typeof eventTypes>;

function EventHandler(props: FormProps, listeners: { [key: string]: Array<(data: any) => void> }) {
  const handlers = {
    onCancel: (event, data) => {
      if (event === eventTypes.DISMISS) {
        if (typeof props.onDismiss === 'function') {
          props.onDismiss();
        }
      }
    },
  };

  const handle = (event: EventTypesType, data?: any) => {
    Object.values(handlers).forEach((func) => func(event, data));
    if (listeners[event]) {
      listeners[event].forEach((listener) => listener(data));
    }
  };

  return {
    handle,
  };
}

type ValidatorType = (() => boolean) | (() => Promise<boolean>);
type SuccessFuncType = (() => boolean) | (() => Promise<boolean>);
type BackFuncType = (() => boolean) | (() => Promise<boolean>);

export default function useFormContainer(props: FormProps): FormContextType {
  const { children, startPage } = props;
  const listeners = useRef({});
  const validatorsRef = useRef<Map<string, ValidatorType>>(new Map());
  const successRef = useRef<SuccessFuncType>(null);
  const backRef = useRef<BackFuncType>(null);

  useEffect(() => {
    if (startPage > 0 && startPage < children.length) {
      setActivePage(startPage);
    }
  }, [startPage]);

  const validate = async () => {
    if (validatorsRef.current?.size === 0) {
      return true;
    }
    const validationResults = await Promise.all(
      Array.from(validatorsRef.current?.values()).map((validator) => {
        return validator();
      }),
    );
    return validationResults.every((result) => result === true);
  };

  const nextPage = async (page: number, dismissValidation: boolean) => {
    if (typeof successRef.current === 'function') {
      setState((state) => {
        state.awaitingValidation = true;
        state.finished = true;
      });
      try {
        const valid = await successRef.current();
        if (valid) {
          successRef.current = null;
        } else {
          setState((state) => {
            state.finished = false;
          });
        }
        setState((state) => {
          state.awaitingValidation = false;
        });
        return;
      } catch (e) {
        setState((state) => {
          state.finished = false;
          state.awaitingValidation = false;
        });
      }
    }
    let canProceed = true;

    if (!dismissValidation && validatorsRef.current?.size > 0) {
      setState((state) => {
        state.awaitingValidation = true;
      });
      try {
        canProceed = await validate();
        setState((state) => {
          state.awaitingValidation = false;
        });
      } catch (e) {
        canProceed = false;
        setState((state) => {
          state.awaitingValidation = false;
        });
      }
    }

    setState((state) => {
      if (!canProceed) return;

      if (typeof page === 'number') {
        if (page >= 0 && page <= children.length - 1) {
          state.activePage = page;
          state.continueBlockedExternally = false;
          validatorsRef.current?.clear();
          successRef.current = null;
          sendEvent(eventTypes.NEXT_PAGE);
        }
        /**
         * if the page is null or undefined and there is available page lets move on.
         */
      } else if (state.activePage < state.pages.length - 1) {
        if (state.activePage === state.pages.length - 2) {
          state.finished = true;
        }
        state.activePage++;
        state.continueBlockedExternally = false;
        validatorsRef.current?.clear();
        successRef.current = null;
        sendEvent(eventTypes.NEXT_PAGE);
      }
    });
  };

  const cancelValidation = () => {
    setState((state) => {
      state.awaitingValidation = false;
    });
  };

  const goBack = async () => {
    sendEvent(eventTypes.BACK);
    if (typeof backRef.current === 'function') {
      await backRef.current();
      backRef.current = null;
      return;
    }
    setState((state) => {
      if (state.activePage === 0 || state.activePage === startPage) {
        state.showLeaveConfirmation = true;
      } else {
        const page = state.activePage - 1;
        setActivePage(page);
        // Reset override functions when going back to prevent stale functions from executing
        successRef.current = null;
        validatorsRef.current = null;
      }
    });
  };

  const setActivePage = (page: number) => {
    if (page < 0) return;
    if (page >= children.length) return;

    setState((draft) => {
      draft.activePage = page;
      draft.continueBlockedExternally = false;
      draft.loaderMessages = {};
      draft.confirmButtonTitle = 'Continue';
      draft.cancelButtonTitle = 'Discard Changes';
    });

    // Always reset override functions when changing pages to prevent stale functions
    successRef.current = null;
    validatorsRef.current?.clear();
  };

  const setCancelButtonVisible = (visible: boolean) => {
    setState((state) => {
      state.showCancel = visible;
    });
  };

  const sendEvent = (event: EventTypesType, data?: any) => {
    EventHandler(props, listeners.current).handle(event, data);
  };

  const addFormEventListener = (event: EventTypesType, listener: (data: any) => void) => {
    if (!listeners.current[event]) {
      listeners.current[event] = [];
    }
    if (!listeners.current[event].includes(listener)) {
      listeners.current[event].push(listener);
    }
  };

  const removeFormEventListener = (event: EventTypesType, listener: (data: any) => void) => {
    if (!listeners.current[event]) {
      return;
    }
    if (!listeners.current[event].includes(listener)) {
      return;
    }
    const indexToRemove = listeners.current[event].indexOf(listener);
    listeners.current[event].splice(indexToRemove, 1);
  };

  const setValidation = (validator: ValidatorType) => {
    if (!validator) {
      validatorsRef.current?.clear();
      return;
    }
    const validatorKey = 'validator';
    validatorsRef.current?.clear();
    validatorsRef.current?.set(validatorKey, validator);
  };

  const addValidation = (validator: ValidatorType, key: string) => {
    const validatorKey = key;
    validatorsRef.current?.set(validatorKey, validator);
  };

  const overrideNextFunction = (onNext: SuccessFuncType) => {
    successRef.current = onNext;
  };

  const overrideBackFunction = (onBack: SuccessFuncType) => {
    backRef.current = onBack;
  };

  const setLoaderMessages = (loaderMessages) => {
    setState((state) => {
      state.loaderMessages = loaderMessages;
    });
  };

  const setShowLeaveConfirmation = (show: boolean) => {
    setState((state) => {
      state.showLeaveConfirmation = show;
    });
  };

  const setConfirmButtonTitle = (title) => {
    setState((state) => {
      state.confirmButtonTitle = title;
    });
  };

  const setCancelButtonTitle = (title) => {
    setState((state) => {
      state.cancelButtonTitle = title;
    });
  };

  const setContinueBlocked = (blocked: boolean) => {
    setState((state) => {
      state.continueBlockedExternally = blocked;
    });
  };

  const [state, setState] = useImmer<FormContextType>({
    pages: Children.toArray(null),
    steps: null,
    finished: false,
    activePage: 0,
    showCancel: true,
    loaderMessages: {},
    showLeaveConfirmation: false,
    continueBlockedExternally: false,
    confirmButtonTitle: 'Continue',
    cancelButtonTitle: 'Discard Changes',
    setConfirmButtonTitle,
    setCancelButtonTitle,
    sendEvent,
    addFormEventListener,
    removeFormEventListener,
    overrideNextFunction,
    overrideBackFunction,
    nextPage,
    validate,
    goBack,
    cancelValidation,
    setActivePage,
    setValidation,
    addValidation,
    setLoaderMessages,
    setCancelButtonVisible,
    setShowLeaveConfirmation,
    setContinueBlocked,
  });

  const isStepClickable = (page: number) => {
    if (props.isEditMode) {
      if (page <= 0) return false;
      return page < children.length - 1;
    }

    return true;
  };

  useEffect(() => {
    setState((state) => {
      const steps = [];
      Children.forEach(props.children, (child: FormPageType, index) => {
        if (child.type.formConfig?.title) {
          steps.push({
            index,
            clickable: isStepClickable(index),
            name: child.type.formConfig?.title,
          });
        }
      });
      state.pages = children;
      state.steps = steps;
    });
  }, [children]);

  return state;
}
